import React from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import { get } from "lodash";
import styled from "styled-components";
import { Table, Text, Box, Card, Badge } from "orcs-design-system";
import { getLoginHistoryForCurrentUser as getLoginHistoryForCurrentUserQuery } from "src/queries/loginHistory.graphql";
import LoadingPage from "src/components/LoadingPage";

const MonospaceText = styled(Text)`
  font-family: monospace;
  font-size: 0.85em;
  cursor: help;
`;

const CompactText = styled(Text)`
  font-size: 0.9em;
  white-space: nowrap;
  cursor: help;
`;

const LOGIN_HISTORY_LIMIT = 10;

const getLoginStatusColor = (type) => {
  switch (type) {
    case "s":
      return "success";
    case "f":
    case "fp":
    case "fu":
      return "danger";
    case "slo":
      return "neutral";
    default:
      return "neutral";
  }
};

// Cell component for date/time
const DateCell = ({ cell }) => {
  const value = cell.getValue();
  const date = new Date(value);
  return (
    <Text>
      {date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}
    </Text>
  );
};

DateCell.propTypes = {
  cell: PropTypes.shape({
    getValue: PropTypes.func.isRequired,
  }).isRequired,
};

// Cell component for location
const LocationCell = ({ row }) => {
  const { city, country } = row.original;
  if (!city && !country) {
    return <Text color="muted">Unknown</Text>;
  }
  return (
    <Text>{city && country ? `${city}, ${country}` : city || country}</Text>
  );
};

LocationCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
    }),
  }).isRequired,
};

// Cell component for IP address
const IpAddressCell = ({ cell }) => {
  const value = cell.getValue();
  if (!value) {
    return <Text color="muted">N/A</Text>;
  }

  // Truncate long IPv6 addresses for better readability
  const displayValue = value.length > 30 ? `...${value.slice(-20)}` : value;

  return <MonospaceText title={value}>{displayValue}</MonospaceText>;
};

IpAddressCell.propTypes = {
  cell: PropTypes.shape({
    getValue: PropTypes.func.isRequired,
  }).isRequired,
};

// Cell component for user agent
const UserAgentCell = ({ cell }) => {
  const value = cell.getValue();
  if (!value) {
    return <Text color="muted">Unknown</Text>;
  }

  // Parse user agent to extract browser and OS
  const parseUserAgent = (ua) => {
    // Extract browser
    const browserMatch = ua.match(
      /(Chrome|Firefox|Safari|Edge|Opera)\/?([\d.]+)/i
    );
    const browser = browserMatch
      ? `${browserMatch[1]} ${browserMatch[2].split(".")[0]}`
      : "";

    // Extract OS
    const osMatch = ua.match(
      /(Mac OS X|Windows NT|Linux|Android|iOS) ([\d._]+)?/i
    );
    const os = osMatch
      ? osMatch[1].replace("Mac OS X", "macOS").replace("Windows NT", "Windows")
      : "";

    // Handle cases where we might have only browser or only OS
    if (browser && os) {
      return `${browser} / ${os}`;
    }
    if (browser) {
      return browser;
    }
    if (os) {
      return os;
    }
    return value.substring(0, 35);
  };

  const displayValue = parseUserAgent(value);

  return <CompactText title={value}>{displayValue}</CompactText>;
};

UserAgentCell.propTypes = {
  cell: PropTypes.shape({
    getValue: PropTypes.func.isRequired,
  }).isRequired,
};

// Cell component for status
const StatusCell = ({ cell, row }) => {
  const value = cell.getValue();
  const { description } = row.original;
  return (
    <Badge color={getLoginStatusColor(value)}>{description || value}</Badge>
  );
};

StatusCell.propTypes = {
  cell: PropTypes.shape({
    getValue: PropTypes.func.isRequired,
  }).isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      description: PropTypes.string,
    }),
  }).isRequired,
};

const PersonLoginHistory = () => {
  const { data, loading, error } = useQuery(
    getLoginHistoryForCurrentUserQuery,
    {
      variables: {
        limit: LOGIN_HISTORY_LIMIT,
      },
      fetchPolicy: "cache-first",
    }
  );

  const loginHistoryResponse = get(data, "getLoginHistoryForCurrentUser", {});
  const loginHistory = get(loginHistoryResponse, "results", []);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Card>
        <Box p={3}>
          <Text color="danger">
            Failed to load login history. Please try again later.
          </Text>
        </Box>
      </Card>
    );
  }

  if (!loginHistory || loginHistory.length === 0) {
    return (
      <Card>
        <Box p={3}>
          <Text>No login history available.</Text>
        </Box>
      </Card>
    );
  }

  const tableColumns = [
    {
      header: "Date & Time",
      accessorKey: "date",
      Cell: DateCell,
      size: 180,
      enableSorting: true,
    },
    {
      header: "Location",
      id: "location",
      Cell: LocationCell,
      size: 200,
      enableSorting: false,
    },
    {
      header: "IP Address",
      accessorKey: "ip",
      Cell: IpAddressCell,
      size: 180,
      enableSorting: false,
    },
    {
      header: "Device / Browser",
      accessorKey: "userAgent",
      Cell: UserAgentCell,
      size: 250,
      enableSorting: false,
    },
    {
      header: "Status",
      accessorKey: "type",
      Cell: StatusCell,
      size: 160,
      enableSorting: true,
    },
  ];

  return (
    <Box width="100%" height="100%">
      <Card elevation={0} height="100%">
        <Box p={4} height="100%">
          <Box>
            <Text as="h1" fontSize={6} fontWeight="bold" mb={2}>
              Login History
            </Text>
            <Text color="greyDark" fontSize={2}>
              Your last {LOGIN_HISTORY_LIMIT} login activities. This shows when
              and where you logged into TeamForm.
            </Text>
          </Box>
          <Table
            columns={tableColumns}
            data={loginHistory}
            enableSorting
            enableColumnResizing={false}
            enableRowSelection={false}
            enableMultiRowSelection={false}
            enableColumnFilters={false}
            enableGlobalFilter={false}
            enableHiding={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableExpanding={false}
            showPagination={false}
            rowsPerPageOptions={[10]}
            muiTableBodyRowProps={() => ({
              hover: true,
              sx: {
                "&:nth-of-type(odd)": {
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                },
              },
            })}
            displayColumnDefOptions={{
              "mrt-row-expand": {
                size: 0,
                enableHiding: true,
                muiTableHeadCellProps: {
                  sx: { display: "none" },
                },
                muiTableBodyCellProps: {
                  sx: { display: "none" },
                },
              },
            }}
            initialState={{
              density: "comfortable",
              sorting: [{ id: "date", desc: true }],
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default PersonLoginHistory;
